export const CURRENCY = "R";
export const formatPrice = (n: number) =>
  `${CURRENCY}${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export type Spec = { label: string; value: string };
export type Variant = { name: string; options: string[] };

export type Product = {
  id: string;
  slug: string;
  name: string;
  sku: string | null;
  brand: string | null;
  category: string | null;
  subcategory: string | null;
  short_description: string | null;
  description: string | null;
  images: string[];
  standard_price: number;
  physio_price: number | null;
  sale_price: number | null;
  stock_quantity: number;
  features: string[];
  specifications: Spec[];
  suitable_for: string[];
  tags: string[];
  body_parts: string[];
  conditions: string[];
  variants: Variant[];
  related_slugs: string[];
  bought_together_slugs: string[];
  sides: string[];
  sizes: string[];
  colours: string[];
  delivery_info: string | null;
  recommendation_why: string | null;
  recommendation_conditions: string | null;
  recommendation_advice: string | null;
  recommendation_precautions: string | null;
  recommendation_tips: string | null;
  featured: boolean;
  physio_recommended: boolean;
  physio_purchase_enabled: boolean;
  on_sale: boolean;
  active: boolean;
  sort_order: number;
  seo_title: string | null;
  seo_description: string | null;
  created_at: string;
  units_sold?: number | null;
  view_count?: number | null;
  rating?: number | null;
  rating_count?: number | null;
};


export type PurchaseType = "standard" | "physio";

export const PURCHASE_LABEL: Record<PurchaseType, string> = {
  standard: "Standard Purchase",
  physio: "Physio Guided Purchase",
};

export const PHYSIO_INCLUDES = [
  "Physiotherapist guidance",
  "Product fitting advice",
  "Correct sizing advice where applicable",
  "Safe usage education",
  "Rehabilitation recommendations",
];

export const STANDARD_INCLUDES = ["Product only", "Delivery"];

export type CategoryNode = { name: string; children?: string[] };

export const CATEGORIES: CategoryNode[] = [
  { name: "Mobility Aids", children: ["Wheelchairs", "Walking Frames", "Walking Sticks", "Crutches"] },
  {
    name: "Braces & Supports",
    children: [
      "Back & Spine Care",
      "Knee Supports",
      "Wrist Supports",
      "Thumb Supports",
      "Ankle Supports",
      "Shoulder Supports",
    ],
  },
  { name: "Recovery Equipment" },
  { name: "Massage & Recovery" },
  { name: "Respiratory Care" },
  { name: "Daily Living Aids" },
  { name: "Physiotherapist Recommended" },
];

export const CATEGORY_NAMES = CATEGORIES.flatMap((c) => [c.name, ...(c.children ?? [])]);

export const SIDES = ["Left", "Right", "Universal"];

export const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "best-selling", label: "Best Selling" },
  { value: "popular", label: "Most Popular" },
  { value: "price-asc", label: "Price Low to High" },
  { value: "price-desc", label: "Price High to Low" },
  { value: "name-asc", label: "Alphabetical, A to Z" },
  { value: "name-desc", label: "Alphabetical, Z to A" },
  { value: "rating", label: "Highest Rated" },
] as const;

export type SortValue = (typeof SORT_OPTIONS)[number]["value"];


export function effectivePrice(p: Product, type: PurchaseType) {
  if (type === "physio" && p.physio_price != null) return p.physio_price;
  if (p.on_sale && p.sale_price != null) return p.sale_price;
  return p.standard_price;
}

export function standardDisplayPrice(p: Product) {
  return p.on_sale && p.sale_price != null ? p.sale_price : p.standard_price;
}

export function stockLabel(p: Product) {
  if (p.stock_quantity <= 0) return { label: "Out of stock", ok: false };
  if (p.stock_quantity <= 3) return { label: `Low stock, ${p.stock_quantity} left`, ok: true };
  return { label: "In stock", ok: true };
}

export function searchMatches(p: Product, q: string) {
  const term = q.trim().toLowerCase();
  if (!term) return true;
  const haystack = [
    p.name,
    p.sku,
    p.brand,
    p.category,
    p.subcategory,
    p.short_description,
    ...(p.tags ?? []),
    ...(p.body_parts ?? []),
    ...(p.conditions ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return term.split(/\s+/).every((t) => haystack.includes(t));
}

export function slugify(s: string) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export const GROUP_SUBTITLES: Record<string, string> = {
  "Mobility Aids": "Helping you move safely and independently.",
  "Braces & Supports": "Support, protect and stabilise joints during recovery and daily activities.",
  "Back & Spine Care": "Products designed to support posture, comfort and spinal health.",
  "Recovery Equipment": "Recovery tools to help you stay active and recover comfortably.",
  "Massage & Recovery": "Massage and soft tissue recovery tools.",
  "Respiratory Care": "Respiratory support for home and everyday use.",
  "Daily Living Aids": "Practical aids that make everyday tasks easier.",
  "Physiotherapist Recommended": "Products our physiotherapists reach for most.",
};

/** Subcategories that also get their own shop section and category page. */
export const SPOTLIGHT_SUBCATEGORIES = ["Back & Spine Care"];

export type ShopGroup = { name: string; slug: string; subtitle?: string; products: Product[] };

export function productsInGroup(products: Product[], name: string) {
  return products.filter((p) => p.category === name || p.subcategory === name);
}

/** Builds shop sections automatically from the categories assigned to products. */
export function buildShopGroups(products: Product[]): ShopGroup[] {
  const categories = new Set<string>();
  const subcategories = new Set<string>();
  for (const p of products) {
    if (p.category) categories.add(p.category);
    if (p.subcategory) subcategories.add(p.subcategory);
  }
  const names = [...categories, ...[...subcategories].filter((s) => SPOTLIGHT_SUBCATEGORIES.includes(s))];
  const unique = [...new Set(names)];
  unique.sort((a, b) => {
    const ia = CATEGORY_NAMES.indexOf(a);
    const ib = CATEGORY_NAMES.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  return unique
    .map((name) => ({
      name,
      slug: slugify(name),
      subtitle: GROUP_SUBTITLES[name],
      products: productsInGroup(products, name),
    }))
    .filter((g) => g.products.length > 0);
}

export function findShopGroup(products: Product[], slug: string): ShopGroup | null {
  return buildShopGroups(products).find((g) => g.slug === slug) ?? null;
}
